import {
    timeEntryTimesRequired,
    type TimeEntryDocType
} from '@ast-data/shared';
import {
    netMinutes,
    timeToMinutes,
    todayIso,
    latestBookableMinutes,
    isAllDaySpan,
    isFutureBooking
} from '../timeTracking/timeMath';
import { timeEntryVariantOf } from '../timeTracking/timeEntryVariant';

/**
 * A row is only valid — and only then allowed to save/sync — once every field
 * it needs is filled in AND the times are consistent: a name, and (where they
 * are there) a start and end with a computable net. Pause defaults to 0, so it
 * is always "filled".
 *
 * The times are only REQUIRED on a Mitarbeiter row: a Gerät may be booked for a
 * day without hours (see timeEntryTimesRequired). Half a span is incomplete
 * either way.
 */
export function isRowComplete(row: TimeEntryDocType): boolean {
    if (!row.workerName.trim()) {
        return false;
    }
    if (!row.startTime && !row.endTime) {
        return !timeEntryTimesRequired(row.entryType);
    }
    return (
        !!row.startTime &&
        !!row.endTime &&
        netMinutes(row.startTime, row.endTime, row.pauseMinutes) !== null
    );
}

/**
 * The validation-rule code for a CONTENT error in one of a row's cells, or
 * null. Non-blocking, so it can tint a cell without breaking the
 * draft-then-fill flow — it flags real conflicts (end ≤ start, future,
 * break ≥ work), NOT empty fields.
 */
export function fieldCellError(
    row: TimeEntryDocType,
    field: string
): string | null {
    const start = timeToMinutes(row.startTime);
    const end = timeToMinutes(row.endTime);
    if (field === 'startTime' || field === 'endTime') {
        if (start !== null && end !== null && end <= start) {
            return field === 'startTime' ? 'startAfterEnd' : 'endBeforeStart';
        }
        const minutes = field === 'startTime' ? start : end;
        const allDay =
            timeEntryVariantOf(row.entryType).showAllDay &&
            isAllDaySpan(row.startTime, row.endTime);
        if (
            !allDay &&
            row.date === todayIso() &&
            minutes !== null &&
            minutes > latestBookableMinutes()
        ) {
            return 'future';
        }
    }
    if (field === 'pauseMinutes') {
        if (
            start !== null &&
            end !== null &&
            end > start &&
            row.pauseMinutes >= end - start
        ) {
            return 'pauseExceedsWork';
        }
    }
    return null;
}

/**
 * The first HARD content-error rule code for a row (end ≤ start, break ≥ work,
 * or a future time), or null. endTime is checked before startTime so an
 * inverted span reports `endBeforeStart` — the same code the office save path
 * emits.
 */
export function rowContentError(row: TimeEntryDocType): string | null {
    for (const field of ['endTime', 'startTime', 'pauseMinutes'] as const) {
        const code = fieldCellError(row, field);
        if (code) {
            return code;
        }
    }
    return null;
}

/**
 * Why a field row can't be saved yet — a validation rule code, the sentinel
 * `'incomplete'`, or `'future'` — or null when it's savable. The field grid's
 * single save guard, mirroring the office `saveTimeEntry` checks so both
 * variants block the same rows with the same messages. Overlap is checked
 * separately by the caller because it needs the RxDB collection.
 */
export function fieldRowSaveError(row: TimeEntryDocType): string | null {
    const content = rowContentError(row);
    if (content) {
        return content;
    }
    if (!isRowComplete(row)) {
        return 'incomplete';
    }
    if (
        isFutureBooking(
            row.date,
            row.startTime,
            row.endTime,
            timeEntryVariantOf(row.entryType).showAllDay
        )
    ) {
        return 'future';
    }
    return null;
}
